import React, { useEffect, useState } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { API } from "./App";
import { useNavigate } from "react-router-dom";
import MenuBar from "./MenuBar";
import { getAuthHeader } from "./AuthRoute";

export default function Graphs() {
  const navigate = useNavigate();

  const [authors, setAuthors] = useState([]);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [data, setData] = useState([]);
  const [loadingAuthors, setLoadingAuthors] = useState(true);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    async function fetchAuthors() {
      try {
        const res = await fetch(`${API()}/sondages/authors`, {
          headers: {
            Authorization: getAuthHeader(),
          },
        });
        if (!res.ok) throw new Error("Failed to fetch authors");
        const json = await res.json();
        setAuthors(json);
        setSelectedAuthor(json[0] || null);
      } catch {
        setAuthors([]);
        setSelectedAuthor(null);
      } finally {
        setLoadingAuthors(false);
      }
    }

    fetchAuthors();
  }, []);

  useEffect(() => {
    if (!selectedAuthor) return;
    setLoadingData(true);

    async function fetchData() {
      try {
        const res = await fetch(
          `${API()}/sondages/by-author?author=${encodeURIComponent(selectedAuthor)}`,
          {
            headers: {
              Authorization: getAuthHeader(),
            },
          },
        );
        if (!res.ok) throw new Error("Failed to fetch sondages");
        const json = await res.json();

        const parseDate = (dateString) => {
          if (!dateString || typeof dateString !== "string") {
            console.warn("Invalid date string:", dateString);
            return new Date(NaN);
          }

          const parts = dateString.split(".");
          if (parts.length !== 3) {
            console.warn("Malformed date string:", dateString);
            return new Date(NaN);
          }

          const [day, month, year] = parts;
          return new Date(`${year}-${month}-${day}`);
        };

        // First filter out entries with invalid dates before sorting
        const validEntries = json.filter((d) => {
          const date = parseDate(d.date_sondage);
          return !isNaN(date.getTime());
        });

        // Sort by date
        const sortedData = validEntries.sort((a, b) => {
          const dateA = parseDate(a.date_sondage).getTime();
          const dateB = parseDate(b.date_sondage).getTime();
          return dateA - dateB;
        });

        const mappedData = sortedData.map((d, i) => {
          const date = parseDate(d.date_sondage);
          return {
            ...d,
            date: date,
            formattedDate: date.toLocaleDateString("fr-CH"),
            url: d.sondage_url,
            x: date.getTime(),
            y: i + 1, // This will now be in proper chronological order
            index: i + 1,
            sondage_name: d.sondage_name,
          };
        });

        console.log("Processed data:", mappedData);
        setData(mappedData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setData([]);
      } finally {
        setLoadingData(false);
      }
    }

    fetchData();
  }, [selectedAuthor]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleDateString("fr-CH");
  };

  const handlePointClick = (point) => {
    if (point.payload.url) {
      // window.open(point.payload.url, "_blank");
      console.log(point._id);
      window.open(`/all-sondages/sondage/${point._id}/docnbr/0`);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: "#fff",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
      >
        <p>
          <strong>Survey:</strong> {data.sondage_name}
        </p>
        <p>
          <strong>Date:</strong> {data.formattedDate}
        </p>
        <p>
          <strong>Index:</strong> {data.index}
        </p>
        <p>
          <small>Click to open survey</small>
        </p>
      </div>
    );
  };

  return (
    <div style={{ position: "fixed", top: 0, width: "100%", zIndex: 1000 }}>
      <MenuBar></MenuBar>
      <div
        style={{
          top: "70px",
          width: "100%",
          padding: 20,
          maxWidth: 1000,
          position: "relative",
        }}
      >
        <h2>Sondages by Author</h2>
        {loadingAuthors ? (
          <p>Loading authors...</p>
        ) : authors.length === 0 ? (
          <p>No authors found.</p>
        ) : (
          <select
            value={selectedAuthor || ""}
            onChange={(e) => setSelectedAuthor(e.target.value)}
            style={{ marginBottom: 20 }}
          >
            {authors.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        )}

        {loadingData && <p>Loading data...</p>}

        {!loadingData && data.length === 0 && <p>No sondages found.</p>}

        {data.length > 0 && (
          <div style={{ width: "100%", height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart
                margin={{ top: 20, right: 20, bottom: 40, left: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={["dataMin", "dataMax"]}
                  tickFormatter={formatDate}
                  name="Date"
                  label={{ value: "Date", position: "bottom" }}
                />
                <YAxis
                  dataKey="y"
                  name="Index"
                  label={{
                    value: "Chronological Order",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Scatter
                  name="Sondages"
                  data={data}
                  fill={"#9739d6"}
                  shape="point"
                  onClick={handlePointClick}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
